import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { PageEvent } from '@angular/material/paginator';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { finalize, Subject, take } from 'rxjs';
import { BiasingPolicyService } from '../../services/biasing-policy.service';
import { NotificationService } from '../../services/notification.service';
import { LoadingComponent } from "../../shared/loading/loading.component";
import { ModalComponent } from '../../shared/modal/modal.component';
import { SearchBarComponent } from "../../shared/searchbar/searchbar.component";
import { SharedTableComponent } from "../../shared/shared-table/shared-table.component";

@Component({
  selector: 'app-biasing-policy',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatSortModule,
    MatCardModule,
    SharedTableComponent,
    SearchBarComponent,
    LoadingComponent
],
  templateUrl: './biasing-policy.component.html',
  styleUrls: ['./biasing-policy.component.scss']
})
export class BiasingPolicyComponent implements OnInit, OnDestroy {

  constructor(
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly biasingService: BiasingPolicyService,
    private readonly notificationService: NotificationService
  ) {}

  isLoading = false;
  hasError = false;
  showFullScreenLoading = false; //loading full screen

  biasingPolicyList$ = new Subject<void>();

  // Stato query
  sortBy = 'name:asc';
  sortField = '';
  sortDirection = '';
  searchQuery = '';

  // Paginazione server-side
  pageIndex = 0;
  pageSize = 10;
  totalItems = 0;

  // DataSource
  dataSourceBiasingPolicy = new MatTableDataSource<any>();

  ngOnInit(): void {
    this.loadBiasingList(this.pageIndex, this.pageSize, this.sortBy, this.searchQuery);
  }

  loadBiasingList(pageIndex: number, pageSize: number, sortBy: string, biasingFilter: string) {
    this.isLoading = true;
    this.hasError = false;
    this.biasingService.getBiasingList(pageIndex, pageSize, sortBy, biasingFilter).pipe(finalize(() => { 
      this.isLoading = false;
      this.biasingPolicyList$.next();
     })).subscribe({
      next: (response) => {
        const content = response?.content ?? [];
        this.dataSourceBiasingPolicy.data = content;
        this.totalItems = response?.totalElements ?? 0;

        // allinea pagina e size con il BE
        this.pageIndex = (response?.number ?? pageIndex) ?? 0;
        this.pageSize  = (response?.size ?? pageSize) ?? this.pageSize;
      },
      error: (err) => {
        console.log('errore nel recupero dei dati', err);
        this.dataSourceBiasingPolicy.data = [];
        this.totalItems = 0;
        this.hasError = true;
      }
    });
  }

  goToDetail(row: any) {
    localStorage.setItem('selectedBiasingRow', JSON.stringify(row));
    this.router.navigate(['/biasing-policy/detail', row.id, encodeURIComponent(row.name)]);
  }

  // Filtro: riparte sempre da pagina 0
  onFilterChanged(value: any) {
    this.searchQuery = value?.name ?? '';
    this.pageIndex = 0;
    this.loadBiasingList(this.pageIndex, this.pageSize, 'name:asc', this.searchQuery);
  }

  // Ordinamento: riparte sempre da pagina 0
  onSortChanged(sort: Sort) {
    this.sortField = sort.active;
    this.sortDirection = sort.direction || 'asc';
    this.sortBy = `${this.sortField}:${this.sortDirection}`;
    this.pageIndex = 0;
    this.loadBiasingList(this.pageIndex, this.pageSize, this.sortBy, this.searchQuery);
  }

  // Cambio pagina / page size (server-side)
  onPageChanged(ev: PageEvent) {
    this.pageIndex = ev.pageIndex;
    this.pageSize  = ev.pageSize;
    this.loadBiasingList(this.pageIndex, this.pageSize, this.sortBy, this.searchQuery);
  }

  openDialog(event: { data: any; type: 'add' | 'edit' | 'delete' }) {
    const dialogRef = this.dialog.open(ModalComponent, {
      width: '400px',
      data: {
        action: event.type,
        payload: event.data,
        visibleFields: ['name', 'description'],
        readonlyFields: { edit: ['name'] },
        labels: {
          name: 'Nome',
          description: 'Descrizione'
        }
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;

      this.showFullScreenLoading = true;

      if (event.type === 'add') {
        this.biasingService.addNewBiasingPolicy(result).pipe(finalize(() => { this.showFullScreenLoading = false; })).subscribe({
          next: () => {
            // resta sulla pagina corrente
            this.loadBiasingList(this.pageIndex, this.pageSize, this.sortBy, this.searchQuery);
            this.biasingPolicyList$.pipe(take(1)).subscribe(() => {
               this.notificationService.entitySuccess('add', 'Biasing policy');
            })
          },
          error: (err) => {
            console.log('errore nella richiesta', err);
            this.notificationService.entityError('add', 'Biasing policy');
          }
        });

      // EDIT attualmente disabilitato (notAllowEdit = true). 
      /* else if (event.type === 'edit') {
          const index = this.dataSourceBiasingPolicy.data.findIndex( e => e.id === result.id);
          console.log("element", result);
          console.log("indice", index);
          if (index !== -1){
             this.biasingService.editBiasingPolicy(result).subscribe({
              next: () => {
                this.loadBiasingList(0, 10, this.sortBy, this.searchQuery);
              },
              error: (err) => {
                console.log("errore nella richiesta", err);
              }
            });
            
          } 
        }  */

      } else if (event.type === 'delete') {
        this.biasingService.deleteBiasingPolicy(result.id).pipe(finalize(() => { this.showFullScreenLoading = false; })).subscribe({
          next: () => {
            // se sto cancellando l’unico item della pagina corrente, scala pagina
            if (this.pageIndex > 0 && this.dataSourceBiasingPolicy.data.length === 1) {
              this.pageIndex = this.pageIndex - 1;
            }
            this.loadBiasingList(this.pageIndex, this.pageSize, this.sortBy, this.searchQuery);
            this.biasingPolicyList$.pipe(take(1)).subscribe(() => {
               this.notificationService.entitySuccess('delete', 'Biasing policy');
            })
          },
          error: (err) => {
            console.log('errore nella richiesta', err);
            this.notificationService.entityError('delete', 'Biasing policy');
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.biasingPolicyList$.complete();
  }
}